import { Box, IconButton } from "@mui/material";
import KeyboardDoubleArrowRight from "@mui/icons-material/KeyboardDoubleArrowRight";
import { useAsync2 } from "nate-react-api-helpers";
import { useState } from "react";
import { api } from "../../../api/API";
import { Task } from "../../../api/Tasks";
import { Crumb, TaskCrumbs, TrailStep } from "./TaskCrumbs";
import { TaskPanel } from "./TaskPanel";

// TaskPanelStack lets the panel walk into the tasks a task is linked to and back out
// again. Only the panel moves: the page behind it keeps showing the task it opened.
export function TaskPanelStack(props: {
    value: Partial<Task>;
    onClose: () => void;

    // ancestors is what the page opened the panel out of, outermost first, so the
    // trail can lead back past the task the panel started on
    ancestors?: TrailStep[];

    // onSaved reports the task the page opened
    onSaved: (task: Task) => void;

    // onOpenProject leaves the panel for what the task on screen is filed under
    onOpenProject?: (ref: { task: number; project: number; goal: number }) => void;
}) {
    const [rootTitle, setRootTitle] = useState(props.value.title || "");
    const [stack, setStack] = useState<Crumb[]>([]);
    const top = stack.length > 0 ? stack[stack.length - 1].id : 0;

    // the panel needs the whole task, comments included, and the page behind it only
    // holds what its list carried (see core/task get)
    const openId = top > 0 ? top : (props.value.id || 0);

    const opened = useAsync2(async input => {
        if (!input.id) return null;
        return api.tasks.get({ id: input.id });
    }, { id: openId }, [openId]);

    const crumbs = ([{ id: props.value.id || 0, title: rootTitle }] as Crumb[]).concat(stack);

    // walking back to a task drops everything the panel opened after it; the last
    // crumb is where the panel already is, so it leads nowhere
    const trail = (props.ancestors || []).concat(crumbs.map((crumb, index) => ({
        title: crumb.title,
        onClick: index === crumbs.length - 1
            ? undefined
            : () => setStack(list => list.slice(0, index)),
    })));

    const current = openId > 0 ? opened.result : props.value;

    if (!current) {
        return (
            <Box sx={{ display: "flex", flexDirection: "column", flex: 1, px: 1, pt: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <TaskCrumbs value={trail} />
                    <IconButton size="small" onClick={props.onClose}>
                        <KeyboardDoubleArrowRight fontSize="small" />
                    </IconButton>
                </Box>
                {opened.LoadingOrErrorElement}
            </Box>
        );
    }

    return (
        <TaskPanel
            // a move changes what the panel offers, so it starts over on the new team
            key={(current.id || "new") + "-" + (current.team || 0)}
            value={current}
            crumbs={trail}
            onClose={props.onClose}
            onOpenProject={props.onOpenProject}
            onMoved={opened.reload}
            onOpen={crumb => setStack(list => list.concat(crumb))}
            onSaved={task => {
                if (top === 0) {
                    setRootTitle(task.title);
                    props.onSaved(task);
                    return;
                }

                setStack(list => list.map(crumb =>
                    crumb.id === task.id ? { ...crumb, title: task.title } : crumb));
            }}
        />
    );
}
